
import { SalesData } from '@/utils/licenseService';
import { ResellerOrder } from '@/types/resellerOrder';
import { getResellerOrders } from '@/utils/resellerOrderService';
import { generateRewardOrderNumber, generateRewardSalesOrderNumber } from '@/utils/orderNumberGenerator';

// This utility function converts ResellerOrder objects to SalesData format
export const convertOrdersToSalesData = (orders: ResellerOrder[]): SalesData[] => {
  return orders.map((order): SalesData => {
    // For activated orders, we have a super admin and instance
    const hasSuperAdmin = order.status === 'activated' && order.activatedById;
    
    // Generate order numbers with the appropriate prefixes
    const requestOrderNumber = `RO${order.orderNumber.substring(2)}`;
    const fulfillmentOrderNumber = `RSO${order.orderNumber.substring(2)}`;
    
    return {
      id: order.id,
      invoiceId: order.orderNumber,
      requestOrderNumber,
      fulfillmentOrderNumber,
      superAdminId: hasSuperAdmin ? order.activatedById || 'sa-001' : 'unknown',
      superAdminName: hasSuperAdmin ? 'Master Admin' : 'Unknown',
      instanceId: order.instanceId,
      instanceName: order.domain,
      planName: 'Reseller Pro',
      billingCycle: 'yearly',
      amount: order.paymentAmount,
      date: new Date(order.orderDate),
      status: order.status === 'activated' ? 'paid' : 
             order.status === 'pending' ? 'pending' : 'failed',
      isRecurring: false // New orders are not recurring
    };
  });
};

// Function to get real sales data including processed orders
export const getRealSalesData = async (): Promise<SalesData[]> => {
  // Get orders from our order service
  const orders = await getResellerOrders();
  
  // Convert them to sales data format
  const orderSalesData = convertOrdersToSalesData(orders);
  
  // For now, we'll combine with the mock data to ensure we have enough data to display
  // In a production app, you would only use the real data
  const { mockSalesData } = await import('@/utils/licenseService/salesData');
  
  // Add order numbers to mock data for consistent display
  const enhancedMockData = mockSalesData.map(sale => ({
    ...sale,
    requestOrderNumber: sale.requestOrderNumber || generateRewardOrderNumber(),
    fulfillmentOrderNumber: sale.fulfillmentOrderNumber || generateRewardSalesOrderNumber()
  }));
  
  return [...orderSalesData, ...enhancedMockData];
};

// Function to get the count of pending orders (for notification badges)
export const getPendingOrdersCount = async (): Promise<number> => {
  const orders = await getResellerOrders();
  return orders.filter(order => order.status === 'pending').length;
};
